const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const normalizeId = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    return normalizeId(
      value.doctorId ??
        value.medecinId ??
        value.userId ??
        value.id ??
        value._id
    );
  }
  return String(value).trim();
};

const localDateKey = (value) => {
  if (!value) return null;

  // A calendar-only value already represents a local day. Parsing it as UTC
  // can move it to the previous day in some time zones.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const belongsToDoctor = (item, doctorId) => {
  const expected = normalizeId(doctorId);
  const actual = normalizeId(
    item?.doctorId ?? item?.medecinId ?? item?.doctor ?? item?.medecin
  );

  // These endpoints are already scoped to the authenticated doctor. Keep
  // records that omit the doctor id instead of incorrectly dropping them.
  return !expected || !actual || actual === expected;
};

const appointmentDate = (appointment) =>
  appointment?.startTime ??
  appointment?.dateTime ??
  appointment?.appointmentDate ??
  appointment?.date;

const appointmentKey = (appointment, index) =>
  String(
    appointment?.id ??
      appointment?._id ??
      appointment?.rendezvousId ??
      appointment?.appointmentId ??
      `appointment-${index}`
  );

const roomKey = (room, index) =>
  String(
    room?.appointmentId ??
      room?.rendezvousId ??
      room?.roomId ??
      room?.id ??
      `room-${index}`
  );

const consultationDate = (consultation) =>
  consultation?.consultationDateTime ?? consultation?.createdAt;

const consultationRevenue = (consultation) => {
  const value = Number(
    consultation?.totalPrice ?? consultation?.consultationPrice ?? 0
  );

  return Number.isFinite(value) && value > 0 ? value : 0;
};

export const calculateTodayOverview = ({
  rendezvous = [],
  rooms = [],
  consultations = [],
  doctorId,
  now = new Date(),
} = {}) => {
  const todayKey = localDateKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = localDateKey(yesterday);
  const doctorRendezvous = (Array.isArray(rendezvous) ? rendezvous : []).filter(
    (item) => belongsToDoctor(item, doctorId)
  );
  const doctorRooms = (Array.isArray(rooms) ? rooms : []).filter((item) =>
    belongsToDoctor(item, doctorId)
  );

  const todayAppointments = new Set();
  const yesterdayAppointments = new Set();
  const rendezvousById = new Map();

  doctorRendezvous.forEach((appointment, index) => {
    const key = appointmentKey(appointment, index);
    const status = normalizeText(appointment?.status);
    rendezvousById.set(key, appointment);

    if (["cancelled", "canceled", "no_show", "noshow"].includes(status)) {
      return;
    }

    const dateKey = localDateKey(appointmentDate(appointment));
    if (dateKey === todayKey) {
      todayAppointments.add(key);
    }
    if (dateKey === yesterdayKey) {
      yesterdayAppointments.add(key);
    }
  });

  const waitingRooms = new Set();
  const completedToday = new Set();

  doctorRooms.forEach((room, index) => {
    const status = normalizeText(room?.status);
    const key = roomKey(room, index);

    if (status === "waiting") {
      waitingRooms.add(key);
    }

    if (status !== "completed") return;

    const completedDate =
      room?.completedAt ?? room?.updatedAt ?? room?.createdAt ?? null;
    const linkedAppointment = rendezvousById.get(
      String(room?.appointmentId ?? room?.rendezvousId ?? "")
    );
    const effectiveDate = completedDate ?? appointmentDate(linkedAppointment);

    if (localDateKey(effectiveDate) === todayKey) {
      completedToday.add(key);
    }
  });

  doctorRendezvous.forEach((appointment, index) => {
    if (normalizeText(appointment?.status) !== "completed") return;

    const completedDate =
      appointment?.completedAt ??
      appointment?.updatedAt ??
      appointmentDate(appointment);

    if (localDateKey(completedDate) === todayKey) {
      completedToday.add(appointmentKey(appointment, index));
    }
  });

  const dailyRevenue = new Map();

  (Array.isArray(consultations) ? consultations : [])
    .filter((consultation) => belongsToDoctor(consultation, doctorId))
    .forEach((consultation) => {
      const dateKey = localDateKey(consultationDate(consultation));
      if (!dateKey || dateKey > todayKey) return;

      dailyRevenue.set(
        dateKey,
        (dailyRevenue.get(dateKey) ?? 0) + consultationRevenue(consultation)
      );
    });

  const revenue = dailyRevenue.get(todayKey) ?? 0;
  const historicalRevenue = Array.from(dailyRevenue.entries())
    .filter(([dateKey]) => dateKey < todayKey)
    .map(([, amount]) => amount);
  const averageRevenue = historicalRevenue.length
    ? historicalRevenue.reduce((sum, amount) => sum + amount, 0) /
      historicalRevenue.length
    : 0;
  const revenueDelta = averageRevenue > 0
    ? Math.round(((revenue - averageRevenue) / averageRevenue) * 100)
    : 0;
  const historyKeys = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (6 - index));
    return localDateKey(date);
  });
  const sparklines = {
    appointments: historyKeys.map((dateKey) =>
      doctorRendezvous.filter((appointment) => {
        const status = normalizeText(appointment?.status);
        return (
          !["cancelled", "canceled", "no_show", "noshow"].includes(status) &&
          localDateKey(appointmentDate(appointment)) === dateKey
        );
      }).length
    ),
    waiting: historyKeys.map((dateKey) =>
      doctorRooms.filter((room) =>
        localDateKey(room?.arrivedAt ?? room?.arrivalTime ?? room?.createdAt) === dateKey
      ).length
    ),
    completed: historyKeys.map((dateKey) =>
      doctorRendezvous.filter((appointment) => {
        if (normalizeText(appointment?.status) !== "completed") return false;
        const completedDate =
          appointment?.completedAt ??
          appointment?.updatedAt ??
          appointmentDate(appointment);
        return localDateKey(completedDate) === dateKey;
      }).length
    ),
    revenue: historyKeys.map((dateKey) => dailyRevenue.get(dateKey) ?? 0),
  };

  return {
    appointments: todayAppointments.size,
    appointmentsDelta: todayAppointments.size - yesterdayAppointments.size,
    waiting: waitingRooms.size,
    completed: completedToday.size,
    revenue,
    averageRevenue,
    revenueDelta,
    sparklines,
  };
};
